import classnames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { Label } from 'web-ui-components/lib/atoms/forms';
import { Icon } from 'web-ui-components/lib/atoms/iconography';
import { Popover } from 'web-ui-components/lib/atoms/popovers';

import { removePayment, updateAmount, updateHmrcRef, updateReference } from '../../../../states/bacsCredit/actions';
import { getPaymentById } from '../../../../states/bacsCredit/selectors';

import { bacsReferenceMaxLength } from '../../../../config';
import { payeePropTypes } from '../../../../sharedComponents/PayeeList/propTypes';
import ToAccount from '../../../../sharedComponents/ToAccount/ToAccount';
import { toAccountPropType } from '../../../../states/accounts/propTypes';
import styles from '../BacsPaymentStyles';
import HmrcReference from '../Form/HmrcReference';
import TextField from '../Form/TextField';
import PaymentRow from './PaymentRow';

const mapState = (state, props) => ({
	payment: getPaymentById(state, props.id),
});

const mapDispatch = (dispatch, props) => ({
	handleRemovePayment: () =>
		dispatch(
			removePayment({
				id: props.id,
			})
		),
	handleAmountBlur: (value = '') => {
		if (props.amount !== value) {
			dispatch(
				updateAmount({
					id: props.id,
					amount: value,
				})
			);
		}
	},
	handleReferenceBlur: (value) => {
		dispatch(
			updateReference({
				id: props.id,
				reference: value,
			})
		);
	},
	handleHmrcReferenceBlur: (value) =>
		dispatch(
			updateHmrcRef({
				id: props.id,
				hmrcReference: value,
			})
		),
});

export default ((WrappedComponent) => {
	const CreditPaymentRow = (props) => {
		const renderAccountField = (props) => {
			const {
				content: { toLabel },
				id,
				fromAccount,
				payees,
				toAccounts,
				payment: { toAccount },
				handleToCardClick,
				showValidation,
				updateValidation,
				hideErrorMsg,
				validations,
			} = props;

			return (
				<span>
					<Label className={classnames(styles.main__inputLabel)}>{toLabel}</Label>
					<ToAccount
						id={id}
						htmlFor={`field-to-account-${id}`}
						fromAccount={fromAccount}
						toPayees={payees}
						toAccounts={toAccounts}
						toAccount={toAccount}
						handleCardClick={handleToCardClick}
						showValidation={showValidation}
						validations={validations}
						updateValidation={updateValidation}
						hideErrorMsg={hideErrorMsg}
						disable={!fromAccount}
					/>
				</span>
			);
		};
		const renderReferenceField = (props) => {
			const { id, content, handleReferenceBlur, payment } = props;

			const { reference, isToCreditCard } = payment;
			const { referenceLabel } = content;

			const derivedReference = isToCreditCard ? '' : reference;

			return (
				<span>
					<Label className={classnames(styles.main__inputLabel, 'ref')}>{referenceLabel}</Label>
					<TextField
						id={`field-reference--${id}`}
						onBlur={handleReferenceBlur}
						maxLength={bacsReferenceMaxLength}
						value={derivedReference}
						disabled={isToCreditCard}
						cyLabel="bacs-debit-reference"
						dataTestId="reference"
					/>
				</span>
			);
		};
		const renderHMRCRefOrTransactionCodeField = (props) => {
			const {
				content: { hmrcReferenceLabel, hmrcReferencePopperBody, hmrcReferenceInvalid, isHmrcPopoverOpen },
				id,
				showValidation,
				handleHmrcReferenceBlur,
				toggleHmrcPopover,
				validations,
				updateValidation,
				hideErrorMsg,
				payment: { hmrcReference },
			} = props;
			return (
				<span>
					<Label className={styles.main__inputLabel}>{hmrcReferenceLabel}</Label>
					<span className={styles.main__popper}>
						<Popover
							className="u-margin-left-1"
							isOpen={isHmrcPopoverOpen || false}
							onClick={toggleHmrcPopover}
							placement="top"
							content={hmrcReferencePopperBody}
						>
							<Icon iconType="InfoOutlineLarge" size="xs" />
						</Popover>
					</span>
					<HmrcReference
						id={`field-hmrc-reference--${id}`}
						hmrcReference={hmrcReference}
						onBlur={handleHmrcReferenceBlur}
						showValidation={showValidation}
						invalidMessage={hmrcReferenceInvalid}
						validations={validations}
						updateValidation={updateValidation}
						hideErrorMsg={hideErrorMsg}
					/>
				</span>
			);
		};

		return (
			<WrappedComponent
				{...props}
				renderReferenceField={renderReferenceField}
				renderAccountField={renderAccountField}
				renderHMRCRefOrTransactionCodeField={renderHMRCRefOrTransactionCodeField}
			/>
		);
	};

	CreditPaymentRow.propTypes = {
		handleRemovePayment: PropTypes.func.isRequired,
		handleAmountBlur: PropTypes.func.isRequired,
		handleToCardClick: PropTypes.func.isRequired,
		handleReferenceBlur: PropTypes.func.isRequired,
		handleHmrcReferenceBlur: PropTypes.func.isRequired,
		id: PropTypes.string.isRequired,
		toAccount: PropTypes.string,
		toAccounts: PropTypes.arrayOf(PropTypes.shape(toAccountPropType)).isRequired,
		fromAccount: PropTypes.string.isRequired,
		payment: PropTypes.shape({
			id: PropTypes.string.isRequired,
			toAccount: PropTypes.string.isRequired,
		}),
		payees: PropTypes.arrayOf(PropTypes.shape(payeePropTypes)).isRequired,
		showRemoveButton: PropTypes.bool.isRequired,
		content: PropTypes.shape({}).isRequired,
		showValidation: PropTypes.bool.isRequired,
		updateValidation: PropTypes.func,
		validations: PropTypes.shape({}),
		hideErrorMsg: PropTypes.func,
		toggleHmrcPopover: PropTypes.func.isRequired,
		templateMode: PropTypes.bool,
	};

	return connect(mapState, mapDispatch)(CreditPaymentRow);
})(PaymentRow);
